import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Shield, Send, Phone, Mail, CheckCircle2, AlertCircle, Upload, Paperclip, Youtube, Instagram, Facebook } from "lucide-react";
import { CONTACT_INFO } from "../data";
import { useLanguage } from "../components/LanguageContext";
import SEO from "../components/SEO";

export default function Contact() {
  const { lang, t } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    description: location.state?.horseName 
      ? (lang === "da" 
          ? `Jeg ønsker yderligere information om ${location.state.horseName}.` 
          : `I would like more information about ${location.state.horseName}.`)
      : "",
    website_url: "" // Honeypot
  });
  const [files, setFiles] = useState([]);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    
    // Client-side validation for files
    if (selectedFiles.length + files.length > 5) {
      setStatus({ type: "error", message: t.contact.form_error_files_limit });
      return;
    }

    const allowedTypes = ["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"];
    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        setStatus({ type: "error", message: `${t.contact.form_error_file_type} (${file.name})` });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setStatus({ type: "error", message: `${t.contact.form_error_file_size} (${file.name})` });
        return;
      }
    }

    setFiles((prev) => [...prev, ...selectedFiles]);
    setStatus({ type: null, message: "" });
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: null, message: "" });

    // Basic validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ type: "error", message: t.contact.form_error_required });
      setLoading(false);
      return;
    }

    try {
      // Create FormData for submission
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("phone", formData.phone);
      submitData.append("email", formData.email);
      submitData.append("description", formData.description);
      submitData.append("website_url", formData.website_url); // Honeypot

      files.forEach((file) => {
        submitData.append("attachments", file);
      });

      const response = await fetch("https://api.dev.auxo.dk/webform/submit", {
        method: "POST",
        body: submitData
      });

      if (!response.ok) {
        throw new Error(lang === "da" ? "Der opstod en fejl ved afsendelse. Prøv igen senere." : "An error occurred while sending. Please try again later.");
      }

      setStatus({
        type: "success",
        message: t.contact.form_success
      });
      setFormData({ name: "", phone: "", email: "", description: "", website_url: "" });
      setFiles([]);
    } catch (err) {
      setStatus({ type: "error", message: err.message });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="bg-[#FCF9F5] text-stone-900 py-24 font-sans-custom">
      <SEO pageKey="contact" />

      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl space-y-6 mb-20">
          <div className="inline-flex items-center gap-3 border border-stone-300 bg-stone-100 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] text-stone-800 rounded-none font-medium font-sans-custom">
            <Shield className="h-3.5 w-3.5 text-stone-700" />
            <span>{t.contact.badge}</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl md:text-6xl uppercase tracking-normal sm:tracking-wide-custom text-stone-900 font-medium">
            {t.contact.title}{" "}
            <span className="italic text-stone-600 lowercase font-display normal-case">
              {t.contact.title_italic}
            </span>{" "}
            {t.contact.title_suffix}
          </h1>
          <p className="text-sm text-stone-600 leading-relaxed font-light tracking-wide-custom font-sans-custom">
            {t.contact.desc}
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-12">
          {/* Contact Form */}
          <div className="lg:col-span-7 border border-stone-200 bg-white p-8 rounded-none shadow-sm">
            <h2 className="font-display text-lg sm:text-xl text-stone-900 uppercase tracking-normal sm:tracking-wide-custom mb-8 font-medium">{t.contact.form_title}</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field (absolutely hidden from humans) */}
              <div className="sr-only absolute -z-50">
                <label htmlFor="website_url">Do not fill this field</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div className="space-y-1">
                  <label htmlFor="name" className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold font-sans-custom">
                    {t.contact.form_name} <span className="text-stone-600">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-transparent border-b border-stone-200 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-800 transition-colors duration-300 rounded-none placeholder-stone-400/50 font-sans-custom"
                    placeholder={t.contact.form_name_placeholder}
                  />
                </div>

                <div className="space-y-1">
                  <label htmlFor="phone" className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold font-sans-custom">
                    {t.contact.form_phone} <span className="text-stone-600">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-transparent border-b border-stone-200 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-800 transition-colors duration-300 rounded-none placeholder-stone-400/50 font-sans-custom"
                    placeholder={t.contact.form_phone_placeholder}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label htmlFor="email" className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold font-sans-custom">
                  {t.contact.form_email} <span className="text-stone-600">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full bg-transparent border-b border-stone-200 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-800 transition-colors duration-300 rounded-none placeholder-stone-400/50 font-sans-custom"
                  placeholder={t.contact.form_email_placeholder}
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="description" className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold font-sans-custom">
                  {t.contact.form_desc} <span className="text-stone-600">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full bg-transparent border-b border-stone-200 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-800 transition-colors duration-300 resize-none rounded-none placeholder-stone-400/50 font-sans-custom"
                  placeholder={t.contact.form_desc_placeholder}
                />
              </div>

              {/* File Upload Field */}
              <div className="space-y-2 pt-2">
                <label className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold block font-sans-custom">
                  {t.contact.form_files}
                </label>
                <div className="relative flex items-center justify-center border border-dashed border-stone-200 bg-stone-50/50 p-6 hover:border-stone-400 transition-colors duration-300 rounded-none">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    onChange={handleFileChange}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                  <div className="text-center space-y-1">
                    <Upload className="mx-auto h-6 w-6 text-stone-400" />
                    <p className="text-xs text-stone-500 font-sans-custom">{t.contact.form_files_placeholder}</p>
                    <p className="text-[10px] text-stone-400 font-sans-custom">{t.contact.form_files_sub}</p>
                  </div>
                </div>

                {/* Attached Files List */}
                {files.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {files.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-stone-50 px-3 py-2 text-xs text-stone-700 border border-stone-200 rounded-none shadow-sm font-sans-custom"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Paperclip className="h-3.5 w-3.5 text-stone-400 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-[10px] text-stone-400">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(idx)}
                          className="text-stone-400 hover:text-red-500 transition-colors duration-300 cursor-pointer font-medium"
                        >
                          {t.contact.form_files_remove}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Status Messages */}
              {status.type && (
                <div
                  className={`flex items-start gap-3 p-4 text-sm rounded-none font-sans-custom ${
                    status.type === "success"
                      ? "border border-emerald-200 bg-emerald-50/50 text-emerald-800"
                      : "border border-red-200 bg-red-50/50 text-red-800"
                  }`}
                >
                  {status.type === "success" ? (
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  )}
                  <span>{status.message}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-stone-800 hover:bg-stone-900 text-stone-50 px-5 py-3 sm:px-6 sm:py-4 text-[10px] sm:text-xs font-semibold uppercase tracking-widest transition-colors duration-300 disabled:opacity-50 rounded-none cursor-pointer font-sans-custom"
              >
                {loading ? (
                  <div className="h-4 w-4 animate-spin border-2 border-stone-50 border-t-transparent rounded-none" />
                ) : (
                  <>
                    <span>{t.contact.form_btn_send}</span>
                    <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Info Sidebar */}
          <div className="lg:col-span-5 space-y-8">
            {/* Contact Persons */}
            <div className="border border-stone-200 bg-white p-8 space-y-6 rounded-none shadow-sm">
              <h2 className="font-display text-lg sm:text-xl text-stone-900 uppercase tracking-normal sm:tracking-wide-custom font-medium">{t.contact.sidebar_title}</h2>
              
              <div className="space-y-6">
                <div className="space-y-2">
                  <h3 className="font-display text-lg text-stone-900 uppercase tracking-wide-custom font-medium">Marcus Heineking</h3>
                  <a
                    href="tel:+4552174997"
                    className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors duration-300 whitespace-nowrap font-sans-custom"
                  >
                    <Phone className="h-4 w-4 text-stone-400" />
                    <span className="font-mono">+45 52 17 49 97</span>
                  </a>
                </div>

                <div className="space-y-2 pt-6 border-t border-stone-100">
                  <h3 className="font-display text-lg text-stone-900 uppercase tracking-wide-custom font-medium">Rikke Heineking</h3>
                  <a
                    href="tel:+4520293430"
                    className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-stone-900 transition-colors duration-300 whitespace-nowrap font-sans-custom"
                  >
                    <Phone className="h-4 w-4 text-stone-400" />
                    <span className="font-mono">+45 20 29 34 30</span>
                  </a>
                </div>
              </div>
            </div>

            {/* General Contact Info */}
            <div className="border border-stone-200 bg-white p-8 space-y-6 rounded-none shadow-sm">
              <h2 className="font-display text-lg sm:text-xl text-stone-900 uppercase tracking-normal sm:tracking-wide-custom font-medium">
                {lang === "da" ? "E-mail & Sociale Medier" : "Email & Social Media"}
              </h2>
              <div className="space-y-6 text-sm font-sans-custom">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-stone-400 mt-0.5" />
                  <div>
                    <span className="text-[10px] text-stone-400 block uppercase tracking-widest font-semibold font-sans-custom">{t.contact.sidebar_email}</span>
                    <a href="mailto:info@equinesport.dk" className="text-stone-600 hover:text-stone-900 transition-colors duration-300 whitespace-nowrap font-sans-custom">
                      info@equinesport.dk
                    </a>
                  </div>
                </div>

                {/* Social Media links */}
                <div className="pt-4 border-t border-stone-100 space-y-3">
                  <span className="text-[10px] text-stone-400 block uppercase tracking-widest font-semibold font-sans-custom">
                    {lang === "da" ? "Følg os" : "Follow us"}
                  </span>
                  <div className="flex items-center gap-3">
                    <a
                      href={CONTACT_INFO.socials.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center border border-stone-200 bg-stone-50 text-stone-600 hover:border-stone-800 hover:text-stone-900 hover:bg-white transition-all duration-300 rounded-none shadow-sm"
                      aria-label="Facebook"
                    >
                      <Facebook className="h-4 w-4" />
                    </a>
                    <a
                      href={CONTACT_INFO.socials.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center border border-stone-200 bg-stone-50 text-stone-600 hover:border-stone-800 hover:text-stone-900 hover:bg-white transition-all duration-300 rounded-none shadow-sm"
                      aria-label="Instagram"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>
                    <a
                      href={CONTACT_INFO.socials.youtube}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center border border-stone-200 bg-stone-50 text-stone-600 hover:border-stone-800 hover:text-stone-900 hover:bg-white transition-all duration-300 rounded-none shadow-sm"
                      aria-label="YouTube"
                    >
                      <Youtube className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
